export type ForensicInvestigation = {
  company_name: string;
  symbol: string;
  sector: string;
  industry: string;
  risk_vector_1: string;
  risk_vector_2: string;
  risk_vector_3: string;
  promoter_architecture: string;
  prior_stints_value: string;
  capital_allocation_governance: string;
  score_strategic_vision: string;
  justification_strategic_vision: string;
  score_capital_allocation: string;
  justification_capital_allocation: string;
  score_governance: string;
  justification_governance: string;
  score_execution_moat: string;
  justification_execution_moat: string;
  score_red_flag_adjustment: string;
  justification_red_flag: string;
  total_management_score: string;
  verdict_category: string;
  verdict_summary: string;
  peer_context: string;
  investigation_date: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type ForensicScoreDimension = {
  key: string;
  label: string;
  score: number;
  max: number;
  justification: string;
  isPenalty?: boolean;
};

export const FORENSIC_DIMENSION_MAX = 25;
export const FORENSIC_TOTAL_MAX = 100;

export function toForensicGaugeScore(totalScore: number): number {
  return Math.min(10, Math.max(0, totalScore / 10));
}

export function parseForensicScore(value: string | undefined): number {
  const parsed = Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getForensicScoreDimensions(
  data: ForensicInvestigation,
): ForensicScoreDimension[] {
  return [
    {
      key: "strategic_vision",
      label: "Strategic Vision",
      score: parseForensicScore(data.score_strategic_vision),
      max: FORENSIC_DIMENSION_MAX,
      justification: data.justification_strategic_vision,
    },
    {
      key: "capital_allocation",
      label: "Capital Allocation",
      score: parseForensicScore(data.score_capital_allocation),
      max: FORENSIC_DIMENSION_MAX,
      justification: data.justification_capital_allocation,
    },
    {
      key: "governance",
      label: "Governance",
      score: parseForensicScore(data.score_governance),
      max: FORENSIC_DIMENSION_MAX,
      justification: data.justification_governance,
    },
    {
      key: "execution_moat",
      label: "Execution & Moat",
      score: parseForensicScore(data.score_execution_moat),
      max: FORENSIC_DIMENSION_MAX,
      justification: data.justification_execution_moat,
    },
    {
      key: "red_flag",
      label: "Red Flag Adjustment",
      score: parseForensicScore(data.score_red_flag_adjustment),
      max: 0,
      justification: data.justification_red_flag,
      isPenalty: true,
    },
  ];
}

export function getForensicScoreColor(
  score: number,
  isPenalty = false,
  max = FORENSIC_DIMENSION_MAX,
): string {
  if (isPenalty) {
    return score < 0 ? "#ef4444" : "#94a3b8";
  }
  const ratio = max > 0 ? score / max : 0;
  if (ratio >= 0.8) return "#00A85F";
  if (ratio >= 0.6) return "#f59e0b";
  return "#ef4444";
}

export function getVerdictBadgeClass(category: string): string {
  const normalized = category.toLowerCase();
  if (
    normalized.includes("avoid") ||
    normalized.includes("weak") ||
    normalized.includes("concern")
  ) {
    return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
  }
  if (
    normalized.includes("strong") ||
    normalized.includes("quality") ||
    normalized.includes("favourable") ||
    normalized.includes("favorable")
  ) {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
  }
  return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
}
